import { Button } from '@shared/components/ui/button';
import { EnterIcon, EscIcon } from '@shared/components/ui/icons';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface ImpersonateModalRef {
  /**
   * Abre la modal de confirmación de impersonación.
   * @param onConfirm Callback que se ejecutará si el usuario acepta.
   * @param clientName Nombre del cliente a impersonar.
   */
  open: (onConfirm: () => void, clientName?: string) => void;
}

export const ImpersonateModal = forwardRef<ImpersonateModalRef>((_, ref) => {
  const { t } = useTranslation('clientsModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const confirmRef = useRef<() => void>(() => {});
  const [clientName, setClientName] = useState<string>('');

  useImperativeHandle(ref, () => ({
    open: (onConfirm: () => void, name?: string) => {
      confirmRef.current = onConfirm;
      setClientName(name || '');
      modalRef.current?.openModal();
    },
  }));

  const handleConfirm = () => {
    confirmRef.current();
    confirmRef.current = () => {};
    modalRef.current?.closeModal();
  };

  return (
    <ModalCenter ref={modalRef} title={t('modal.impersonate.title')} type="default" width="default">
      <div className="space-y-4">
        <p className="text-sm">{t('modal.impersonate.message', { name: clientName })}</p>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" size="lg" onClick={() => modalRef.current?.closeModal()}>
            {t('modal.impersonate.buttons.cancel')}
            <EscIcon className="w-4 h-4" />
          </Button>
          <Button variant="primary" size="lg" onClick={handleConfirm}>
            {t('modal.impersonate.buttons.confirm')}
            <EnterIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

ImpersonateModal.displayName = 'ImpersonateModal';
